import { Container } from '~/components/ui/Container'
import { Eyebrow } from '~/components/ui/Eyebrow'
import type { ReactNode } from 'react'
import { RichText } from '~/components/ui/RichText'
import { formatDate } from '~/lib/date'
import type { MediaImage } from '~/lib/media/media-image'
import { PageTitle } from '~/components/ui/PageTitle'
import { pageTop } from '~/components/ui/PageHero'

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/** `audio` is raw HTML, or just a URL of an audio file (the podcast posts). */
const AUDIO_URL = /^https?:\/\/\S+\.(?:mp3|m4a|wav|ogg)$/i

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <p className="mb-0 flex gap-[.5em] text-xs leading-[26px]">
      <span>{label}</span>
      <span>{value}</span>
    </p>
  )
}

/** Shared body of the news and blog detail pages; children render inside the article, after the audio embed. */
export function ArticleLayout({
  kicker,
  author,
  date,
  title,
  image,
  quote,
  html,
  audio,
  search,
  children,
}: {
  kicker?: string
  author?: string
  date: string
  title: string
  image?: MediaImage
  quote?: string
  html?: string
  /** Raw embed HTML (podcast player etc.). */
  audio?: string
  /** Search facets for the Pagefind index: `type` filter, result label, outlet. */
  search?: { type: string; label: string; outlet?: string }
  children?: ReactNode
}) {
  return (
    <Container width="narrow" className={`pb-10 md:pb-5 ${pageTop}`}>
      {/* data-pagefind-*: this article is what the news search indexes
          (docs/architecture.md, "Search"). The kicker and byline are
          metadata, so they're kept out of matching and excerpts. */}
      <article
        {...(search && {
          'data-pagefind-body': true,
          'data-pagefind-filter': 'type[data-search-type]',
          'data-pagefind-meta':
            'date[data-date], label[data-label], outlet[data-outlet]',
          'data-search-type': search.type,
          'data-date': date,
          'data-label': search.label,
          'data-outlet': search.outlet,
        })}
      >
        <div data-pagefind-ignore>
          {kicker && <Eyebrow variant="kicker">{kicker}</Eyebrow>}
          {author && <Meta label="Author:" value={author} />}
          <Meta label="Published on:" value={formatDate(date)} />
        </div>
        <PageTitle size="sm" className="mt-5">
          {title}
        </PageTitle>
        {image && (
          <img
            {...image}
            sizes="(min-width: 992px) 940px, 100vw"
            alt=""
            className="mt-5 h-auto w-full"
          />
        )}
        {quote && (
          <RichText
            html={`<blockquote>${escapeHtml(quote)}</blockquote>`}
            className="mt-5"
          />
        )}
        {html && <RichText html={html} className="py-5" />}
        {audio &&
          (AUDIO_URL.test(audio.trim()) ? (
            <div className="py-5">
              {/* oxlint-disable-next-line jsx-a11y/media-has-caption -- podcast audio, no caption tracks exist */}
              <audio
                controls
                preload="none"
                src={audio.trim()}
                className="w-full"
              />
            </div>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: audio }} />
          ))}
        {children}
      </article>
    </Container>
  )
}
